export type AdjustSender = (delta: number, operationId: string) => Promise<void>;

type Pending = {
  delta: number;
  timer: ReturnType<typeof setTimeout>;
  sender: AdjustSender;
  onError: (delta: number, error: unknown) => void;
};

export class StockCommandQueue {
  private readonly pending = new Map<string, Pending>();
  private readonly chains = new Map<string, Promise<void>>();

  constructor(private readonly delay = 650) {}

  enqueue(key: string, delta: number, sender: AdjustSender, onError: (delta: number, error: unknown) => void): void {
    if (delta === 0) return;
    const existing = this.pending.get(key);
    if (existing) {
      clearTimeout(existing.timer);
      existing.delta += delta;
      existing.timer = setTimeout(() => void this.flush(key), this.delay);
      return;
    }
    const pending: Pending = {
      delta,
      sender,
      onError,
      timer: setTimeout(() => void this.flush(key), this.delay),
    };
    this.pending.set(key, pending);
  }

  async flush(key: string): Promise<void> {
    const pending = this.pending.get(key);
    if (pending) {
      clearTimeout(pending.timer);
      this.pending.delete(key);
      const run = async () => {
        if (pending.delta === 0) return;
        try {
          await pending.sender(pending.delta, crypto.randomUUID());
        } catch (error) {
          pending.onError(pending.delta, error);
        }
      };
      const chain = (this.chains.get(key) ?? Promise.resolve()).then(run, run);
      this.chains.set(key, chain);
    }
    await (this.chains.get(key) ?? Promise.resolve());
  }

  async serialize<T>(key: string, command: () => Promise<T>): Promise<T> {
    await this.flush(key);
    const previous = this.chains.get(key) ?? Promise.resolve();
    let result!: T;
    const run = previous.then(async () => { result = await command(); });
    this.chains.set(key, run.catch(() => undefined));
    await run;
    return result;
  }

  dispose(): void {
    for (const pending of this.pending.values()) clearTimeout(pending.timer);
    this.pending.clear();
  }
}
